/**
 * The verb form's pure half (13-25): which columns a verb's form shows, how a record prefills them, which of them
 * changed, and how a change reads in the confirm. `components/record/VerbForm.tsx` renders it; the 13-20 engine
 * (`buildRecordVerb`) or a closure builder (13-21) decides what is saved.
 *
 * Nothing here names a column: the columns come from the field set (`verbColumns`, kinds, labels, live choices),
 * `Status` from `config/registers.ts`, and the stamps from 13-20's `VERB_STAMPS`.
 *
 * Form values are strings, as the inputs hold them. A day is the date input's own `YYYY-MM-DD` text and is never
 * passed through a `Date` (FIELDSET §1.4); an instant's time sits beside its day under `<column>@time` and becomes
 * a UTC instant only in the engine, by the `Australia/Melbourne` rules (D-13-L-10).
 */

import { REGISTERS } from '../config/registers';
import { fieldOf, verbColumns } from '../config/fieldset/registerFields';
import type { FieldSpec } from '../config/fieldset/registerFields';
import type { FieldKind, RecordFieldValue, RecordRegister, RecordRow, RecordVerbKey, Refusal } from '../types/records';
import { classifyInstant, formatDay, formatInstant, melbourneDayOf } from './fieldValues';
import { INJURED_WORKER_NAME, domainOf, melbourneTimeOf } from './lodgeLayout';
import { COMPUTED_COLUMNS, VERB_STAMPS, isBlank } from './recordVerbs';
import type { EngineVerb } from './recordVerbs';

/** What the inputs hold, keyed by column (and `<column>@time` for an instant's time). */
export type FormValues = Readonly<Record<string, string>>;

/** The key an instant's time is held under. */
export function timeKeyOf(column: string): string {
  return `${column}@time`;
}

/**
 * 13.2-09: does the form show a time beside this column's date? An instant always does (and needs it). A day column
 * that takes a time when known (`timeWhenKnown`: Incidents `OccurredOn` and `ReportedOn`, which the `complete` verb
 * writes) shows one too, as the lodge form does, with the time optional.
 */
export function takesTime(spec: Pick<FieldSpec, 'kind' | 'timeWhenKnown'>): boolean {
  return spec.kind === 'instant' || (spec.kind === 'day-existing' && spec.timeWhenKnown === true);
}

/** The kinds a verb form renders as an input. Attachments and the frozen Person columns are never inputs. */
const INPUT_KINDS: ReadonlySet<FieldKind> = new Set<FieldKind>([
  'text',
  'textarea',
  'choice',
  'instant',
  'day-new',
  'day-existing',
  'upn',
  'position',
]);

/** The field spec of a form column (a programming error if the field set has none). */
export function specOf(register: RecordRegister, column: string): FieldSpec {
  const spec = fieldOf(register, column);
  if (!spec) throw new Error(`verbForm: ${register}.${column} is not in the field set`);
  return spec;
}

/**
 * The columns a verb's form shows by default: the verb's own list (FIELDSET §3), less `Status` (the engine moves
 * it), the verb's stamps (set when you save, e.g. `TriagedAt` / `TriagedByUPN`), the columns it computes (13-27:
 * `NotificationDueAt` on reportability) and any column that is not an input kind.
 */
export function verbInputColumns(register: RecordRegister, verb: RecordVerbKey): string[] {
  const status = REGISTERS[register].fields.status;
  const stamps: Readonly<Record<string, unknown>> = VERB_STAMPS[register]?.[verb as EngineVerb] ?? {};
  const computed = COMPUTED_COLUMNS[register]?.[verb as EngineVerb] ?? [];
  return verbColumns(register, verb).filter((column) => {
    if (column === status || column in stamps || computed.includes(column)) return false;
    const spec = fieldOf(register, column);
    return spec !== undefined && INPUT_KINDS.has(spec.kind);
  });
}

/** The verb's stamps, as their labels ("set when you save"). */
export function stampLabels(register: RecordRegister, verb: RecordVerbKey): string[] {
  const stamps = VERB_STAMPS[register]?.[verb as EngineVerb] ?? {};
  return Object.keys(stamps).map((c) => fieldOf(register, c)?.label ?? c);
}

/** An instant's Melbourne `HH:MM`, or `''` (a stored value with no time of day reads as a day). */
function melbourneTimeOfStored(value: string): string {
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? '' : melbourneTimeOf(new Date(ms));
}

/** The form values a record starts from: the stored value of each column, in the input's own shape. */
export function prefill(register: RecordRegister, row: RecordRow, columns: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const column of columns) {
    const spec = specOf(register, column);
    const stored = row.fields[column];
    const text = isBlank(stored) ? '' : String(stored);
    switch (spec.kind) {
      case 'day-new':
      case 'day-existing':
        out[column] = text ? (melbourneDayOf(text) ?? '') : '';
        // 13.2-09: a day that carries a time of day (a FieldLink or intake `OccurredOn`, say) shows that time, so an
        // edit to the day alone never drops it without anyone seeing.
        if (takesTime(spec)) {
          out[timeKeyOf(column)] = text && out[column] && classifyInstant(text) === 'time-of-day' ? melbourneTimeOfStored(text) : '';
        }
        break;
      case 'instant':
        out[column] = text ? (melbourneDayOf(text) ?? '') : '';
        out[timeKeyOf(column)] = text && out[column] ? melbourneTimeOfStored(text) : '';
        break;
      default:
        out[column] = text;
    }
  }
  return out;
}

/**
 * The `set` the engine gets: only the columns whose input changed from the prefill, in the form's order. A blank
 * input becomes `null` (a clear, which the engine asks a reason for); a day stays its `YYYY-MM-DD` text; an instant
 * goes as `{ day, time }`. 13.2-09: a `timeWhenKnown` day goes as `{ day, time }` when a time is entered, else as its
 * day text (the lodge's `toLodgeValues` shape, which `cleanLodgeValue` reads).
 */
export function changedSet(
  register: RecordRegister,
  columns: readonly string[],
  initial: FormValues,
  current: FormValues,
): Record<string, unknown> {
  const set: Record<string, unknown> = {};
  for (const column of columns) {
    const spec = specOf(register, column);
    if (spec.kind === 'instant') {
      const tk = timeKeyOf(column);
      const day = current[column] ?? '';
      const time = current[tk] ?? '';
      if (day === (initial[column] ?? '') && time === (initial[tk] ?? '')) continue;
      set[column] = day.trim() === '' && time.trim() === '' ? null : { day, time };
      continue;
    }
    if (takesTime(spec)) {
      const tk = timeKeyOf(column);
      const day = current[column] ?? '';
      const time = current[tk] ?? '';
      if (day === (initial[column] ?? '') && time === (initial[tk] ?? '')) continue;
      set[column] = day.trim() === '' && time.trim() === '' ? null : time.trim() === '' ? day : { day, time };
      continue;
    }
    const now = current[column] ?? '';
    if (now === (initial[column] ?? '')) continue;
    set[column] = now.trim() === '' ? null : now;
  }
  return set;
}

/** Columns whose input was answered and is now blank: a clear, which needs a reason (D-13-20-1). */
export function clearedColumns(columns: readonly string[], initial: FormValues, current: FormValues): string[] {
  return columns.filter((c) => (initial[c] ?? '').trim() !== '' && (current[c] ?? '').trim() === '');
}

/** "Not recorded" for a blank: a blank is never "No" (FIELDSET §1.1). */
export const NOT_RECORDED = 'Not recorded';

/** A stored or patched value in words: days `D MMM YYYY`, instants with the Melbourne time (D-13-L-10). */
export function displayValue(spec: Pick<FieldSpec, 'kind'> | { readonly kind: string } | undefined, value: unknown): string {
  if (isBlank(value)) return NOT_RECORDED;
  const text = String(value);
  switch (spec?.kind) {
    case 'day-new':
    case 'day-existing': {
      const label = formatDay(text);
      return label === '—' ? text : label;
    }
    case 'instant': {
      const label = formatInstant(text);
      return label === '—' ? text : label;
    }
    default:
      return text;
  }
}

/** One line of the confirm: `Label: old → new`. */
export interface ChangeLine {
  column: string;
  label: string;
  from: string;
  to: string;
}

/** Every column a save changes, as the confirm lists it (the stamps and `Status` included). */
export function changeLines(register: RecordRegister, row: RecordRow, patch: Readonly<Record<string, RecordFieldValue>>): ChangeLine[] {
  return Object.keys(patch).map((column) => {
    const spec = fieldOf(register, column);
    const next = patch[column];
    return {
      column,
      label: spec?.label ?? column,
      from: displayValue(spec, row.fields[column]),
      to: next === null ? 'cleared' : displayValue(spec, next),
    };
  });
}

/**
 * The form's own checks before the engine: a person (`upn`) column holds a Harmony address, which is the signed-in
 * person's own domain (D-13-24-5). `checkUpn` in the engine still checks the shape.
 *
 * 13.2-09:
 * - With `initial`, a value the person did not change is not checked: it is not sent, and a stored address from
 *   elsewhere (FieldLink's `InjuredWorkerUPN`, which holds the link holder's own email) must not block an unrelated
 *   save.
 * - The injured worker's name refuses an email address (`name-not-email`, the lodge form's words, D-13.2-P-2), so a
 *   record panel and the lodge never disagree.
 */
export function formRefusals(
  register: RecordRegister,
  columns: readonly string[],
  current: FormValues,
  actorUpn: string,
  initial?: FormValues,
): Refusal[] {
  const house = domainOf(actorUpn);
  const out: Refusal[] = [];
  for (const column of columns) {
    const spec = specOf(register, column);
    const value = (current[column] ?? '').trim();
    if (initial && value === (initial[column] ?? '').trim()) continue;
    if (column === INJURED_WORKER_NAME && value.includes('@')) {
      out.push({ code: 'name-not-email', column, message: `${spec.label}: use their name, not an email address.` });
      continue;
    }
    if (spec.kind !== 'upn') continue;
    if (value === '' || !house) continue;
    const domain = domainOf(value);
    if (domain && domain !== house) {
      out.push({ code: 'not-harmony-address', column, message: `${spec.label}: use their Harmony email address.` });
    }
  }
  return out;
}
